import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { AppError } from './errors';

/**
 * Sesiunea trăiește doar în memorie: access token-ul (15 min) aici, refresh-ul în cookie httpOnly
 * (path /api/v1/auth), trimis automat de browser pentru că apelurile sunt same-origin (proxy Vite / Nginx).
 */
export const session = {
  accessToken: null as string | null,
  /** Apelat când refresh-ul eșuează definitiv: AuthProvider trece în `anonymous`. */
  onExpired: (() => {}) as () => void,
};

/** Origine absolută: `Request` din Node (teste) nu acceptă URL-uri relative; în browser e aceeași origine. */
const ORIGIN =
  typeof window !== 'undefined' &&
  window.location &&
  window.location.origin &&
  window.location.origin !== 'null'
    ? window.location.origin
    : 'http://localhost';
export const API_BASE = `${ORIGIN}/api/v1`;

let refreshing: Promise<boolean> | null = null;

/** Un singur refresh pentru toate cererile care primesc 401 în același timp. */
export async function refreshAccessToken(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'same-origin',
        });
        if (!res.ok) return false;
        const body = (await res.json()) as { accessToken?: string };
        if (!body.accessToken) return false;
        session.accessToken = body.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        // Reset sincron: apelurile concurente au primit deja aceeași promisiune; următorul apel ia una nouă.
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

const AUTH_PATHS = ['/auth/login', '/auth/login/totp', '/auth/refresh', '/auth/invitations/'];

const auth: Middleware = {
  async onRequest({ request }) {
    if (session.accessToken) request.headers.set('Authorization', `Bearer ${session.accessToken}`);
    return request;
  },
  async onResponse({ request, response }) {
    const url = new URL(request.url);
    const isAuthCall = AUTH_PATHS.some((p) => url.pathname.includes(p));
    if (response.status === 401 && !isAuthCall && session.accessToken !== null) {
      const problem = await response
        .clone()
        .json()
        .catch(() => ({}) as Record<string, unknown>);
      const code =
        typeof problem.type === 'string'
          ? problem.type.substring(problem.type.lastIndexOf('/') + 1)
          : '';
      // step-up-required nu e o sesiune expirată: îl lăsăm să ajungă la apelant.
      if (code !== 'step-up-required') {
        const ok = await refreshAccessToken();
        if (!ok) {
          session.accessToken = null;
          session.onExpired();
          return response;
        }
        const retry = new Request(request, { headers: new Headers(request.headers) });
        retry.headers.set('Authorization', `Bearer ${session.accessToken}`);
        return fetch(retry);
      }
    }
    return response;
  },
};

// `fetch` se rezolvă la fiecare apel (nu la import), ca testele să îl poată înlocui cu `vi.stubGlobal`.
export const api = createClient<paths>({
  baseUrl: API_BASE,
  credentials: 'same-origin',
  fetch: (request) => globalThis.fetch(request),
});
api.use(auth);

/**
 * Despachetează răspunsul openapi-fetch: datele sau `AppError`.
 * Folosire: `const org = await unwrap(api.GET('/orgs/{id}', { params: { path: { id } } }))`.
 */
export async function unwrap<T>(
  p: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let r: { data?: T; error?: unknown; response: Response };
  try {
    r = await p;
  } catch (e) {
    throw AppError.network(e);
  }
  if (r.error !== undefined || !r.response.ok)
    throw AppError.fromResponse(r.response.status, r.error);
  return r.data as T;
}
